import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import { useRequestShippingStore, INITIAL_STATE } from 'Store/RequestChangeShipping'
import { useSaleOrderStore } from 'Store/SaleOrder'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import moment from 'moment'
import * as Yup from 'yup'
import { REQUEST_STATUS_OPTIONS } from 'Configuration/Paco'
import { usePileStore } from 'Store/Pile'
import { shallow } from 'zustand/shallow'

const VALIDATION_SCHEMA = Yup.object().shape({
  saleOrderId: Yup.number()
    .min(1, 'กรุณาเลือก Sale Order')
    .test('empty-check', 'กรุณาเลือก Sale Order', saleOrderId => !saleOrderId || saleOrderId !== 0)
    .required('กรุณาเลือก Sale Order'),
  quantity: Yup.number()
    .typeError('โปรดระบุเป็นตัวเลข')
    .min(1, 'ระบุจำนวนที่ขอ')
    .required('ระบุจำนวนที่ขอ'),
  pileCode: Yup.string().min(1, 'ระบุรหัสเสา').required('ระบุรหัสเสา'),
  requester: Yup.string().required('ระบุผู้ขอ'),
  requestedAt: Yup.date().required('ระบุวันที่ขอ'),
  requireDate: Yup.date().required('ระบุวันที่ต้องการ'),
})

const ViewModel = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { selected, setSelected, getOne, clearState } = useRequestShippingStore(
    state => ({
      selected: state.selected,
      setSelected: state.setSelected,
      getOne: state.getOne,
      clearState: state.clearState,
    }),
    shallow
  )
  const { create, update } = useRequestShippingStore(
    state => ({
      create: state.create,
      update: state.edit,
    }),
    shallow
  )
  const { saleOrder, getAllSaleOrder } = useSaleOrderStore(
    state => ({
      saleOrder: state.data,
      getAllSaleOrder: state.getAll,
    }),
    shallow
  )
  const { piles, getPile, clearStatePile } = usePileStore(
    state => ({
      piles: state.data,
      getPile: state.getAll,
      clearStatePile: state.clearState,
    }),
    shallow
  )
  const isCreate = location.pathname.includes('create')
  const isView = location.search.includes('view')
  const [disabledSubmitButton, setDisabledSubmitButton] = useState(false)
  const [render, setRender] = useState(false)
  const [openJobStatusChanger, setOpenJobStatusChanger] = useState(false)

  const pileOptions = piles.map((p: any) => ({
    label: p.itemCode,
    value: p.itemCode,
  }))

  const handleOpenJobStatusChanger = () => {
    setOpenJobStatusChanger(true)
  }

  const handleCloseJobStatusChanger = () => {
    setOpenJobStatusChanger(false)
  }

  const onChangeJobStatus = (status: string) => {
    if (status) {
      formik.setFieldValue('jobStatus', status)
    }
  }

  const onSubmitStatus = () => {
    if (formik.values.jobStatus) {
      update(parseInt(id ?? ''), { jobStatus: formik.values.jobStatus }).then(
        ({ success, data }) => {
          if (!success) {
            toast.error(data)
            return
          } else {
            Swal.fire({
              title: 'สำเร็จ',
              text: 'แก้ไขสถานะงานสำเร็จ',
              icon: 'success',
              confirmButtonText: 'ตกลง',
            })
            fetchData()
            handleCloseJobStatusChanger()
          }
        }
      )
    }
  }

  const fetchData = () => {
    getAllSaleOrder()
    getPile()

    if (!isCreate && id) {
      getOne(parseInt(id)).then(({ success, data }) => {
        if (!success) {
          Swal.fire({
            title: 'ดึงข้อมูลไม่สำเร็จ',
            text: 'โปรดลองใหม่อีกครั้ง',
            icon: 'error',
            confirmButtonText: 'ตกลง',
          }).then(() => {
            navigate('/request')
          })
        } else {
          formik.setValues({
            id: data.id,
            requestedAt: moment(data.requestedAt).toDate(),
            requireDate: moment(data.requireDate).toDate(),
            requester: data.requester,
            remark: data.remark,
            saleOrderId: data.project.saleOrderId,
            quantity: data.issueRequestChangeShipping.quantity,
            pileCode: data.issueRequestChangeShipping.pileCode,
            jobStatus: REQUEST_STATUS_OPTIONS[0].key,
          })
          setRender(true)
        }
      })
    } else if (isCreate) {
      setRender(true)
    }
  }

  const cancel = () => {
    navigate('/request')
    clearState()
  }

  const submit = async (values: any) => {
    setDisabledSubmitButton(true)

    if (isCreate) {
      const body: { [key: string]: any } = {
        saleOrderId: values.saleOrderId,
        quantity: values.quantity,
        pileCode: values.pileCode,
        requester: values.requester,
        requestedAt: moment(values.requestedAt).toISOString(),
        requireDate: moment(values.requireDate).toISOString(),
        remark: values.remark,
      }

      create(body).then(({ success, data }) => {
        if (!success) {
          toast.error(data)
          setDisabledSubmitButton(false)
          return
        } else {
          Swal.fire({
            title: 'สำเร็จ',
            text: 'สร้างรายการขอเปลี่ยนแปลงเสาสำเร็จ',
            icon: 'success',
            confirmButtonText: 'ตกลง',
          }).then(() => {
            navigate('/request')
          })
        }
      })
    } else if (!isCreate && id) {
      const allFields = [
        'saleOrderId',
        'quantity',
        'pileCode',
        'requester',
        'requestedAt',
        'requireDate',
        'remark',
      ]
      const body: { [key: string]: any } = {}

      allFields.forEach(field => {
        if (field === 'requestedAt' || field === 'requireDate') {
          if (!moment(selected[field]).isSame(values[field])) {
            body[field] = moment(values[field]).toISOString()
          }
        } else if (selected[field] !== values[field]) {
          body[field] = values[field]
        }
      })

      if (Object.keys(body).length === 0) {
        toast.error('ไม่พบการเปลี่ยนแปลง')
        setDisabledSubmitButton(false)
        return
      }

      update(parseInt(id), body).then(({ success, data }) => {
        if (!success) {
          toast.error(data)
          setDisabledSubmitButton(false)
          return
        } else {
          Swal.fire({
            title: 'สำเร็จ',
            text: 'แก้ไขรายการขอเปลี่ยนแปลงเสาไม่สำเร็จ',
            icon: 'success',
            confirmButtonText: 'ตกลง',
          })
          fetchData()
        }
      })
    }
  }

  const formik = useFormik({
    initialValues: INITIAL_STATE,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: submit,
  })

  const findedSaleOrder = useMemo(() => {
    return saleOrder.find(s => s.id === formik.values.saleOrderId)
  }, [formik.values.saleOrderId, saleOrder])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      clearState()
      clearStatePile()
      setSelected(INITIAL_STATE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    saleOrder: saleOrder.map(s => ({
      label: `${s.code} - ${s.customerTitle}`,
      value: s.id,
    })),
    pileOptions,
    disabledSubmitButton,
    cancel,
    originalJobStatus: selected.jobStatus,
    formik,
    findedSaleOrder,
    isCreate,
    isView,
    render,
    openJobStatusChanger,
    handleOpenJobStatusChanger,
    handleCloseJobStatusChanger,
    onChangeJobStatus,
    onSubmitStatus,
  }
}

export default ViewModel
